import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, BookOpen, Calculator, ClipboardCheck, Building2,
  BarChart3, Briefcase, FileText, Download, ExternalLink,
  Users, DollarSign, TrendingUp, PieChart, Calendar, Clock,
  Shield, CheckCircle2, ChevronRight, GraduationCap, Target,
  Home, LineChart, Wallet, Scale, HelpCircle
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';


const InvestorResourcesPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Guides & White Papers
  const guides = [
    {
      title: 'What Is Value-Add Real Estate Investing?',
      description: 'Overview of renovation strategies, leasing improvements, and operational value creation.',
      icon: Target,
      color: 'amber'
    },
    {
      title: 'Understanding Private Equity Real Estate Funds',
      description: 'Explains fund structures, capital deployment, and investor participation.',
      icon: Building2,
      color: 'blue'
    },
    {
      title: 'Key Risk Factors in Commercial Real Estate',
      description: 'Covers market cycles, leverage, tenant demand, and operational risks.',
      icon: Shield,
      color: 'rose'
    }
  ];

  // Tools & Calculators
  const tools = [
    {
      title: 'Cash Flow Illustration Tool',
      description: 'Demonstrates how rental income and expenses affect distributable cash flow.',
      icon: DollarSign
    },
    {
      title: 'IRR and Equity Multiple Concepts',
      description: 'Visual explanation of how performance metrics are calculated.',
      icon: TrendingUp
    },
    {
      title: 'Hold Period Modeling Overview',
      description: 'Illustrates how time impacts compounding and exit outcomes.',
      icon: Clock
    }
  ];

  // Due Diligence Checklists
  const checklists = [
    {
      title: 'Sponsor Evaluation Checklist',
      description: 'Track record, asset management experience, and reporting practices.',
      icon: Users
    },
    {
      title: 'Market Evaluation Checklist',
      description: 'Employment drivers, housing supply, and rental demand.',
      icon: BarChart3
    },
    {
      title: 'Deal Structure Questions',
      description: 'Capital stack, fees, distribution structure, and risk factors.',
      icon: Scale
    }
  ];

  // Fund structure topics
  const fundTopics = [
    'Capital contributions and capital calls',
    'Distribution waterfalls',
    'Voting and reporting rights',
    'Fee structures'
  ];

  // Resource Card Component
  const ResourceCard = ({ resource, type = 'guide' }) => {
    const colorMap = {
      amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400' },
      blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' },
      rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/20', text: 'text-rose-400' },
      default: { bg: 'bg-[#D4AF37]/10', border: 'border-[#D4AF37]/20', text: 'text-[#D4AF37]' }
    };
    
    const colors = colorMap[resource.color] || colorMap.default;

    return (
      <motion.div
        className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl border border-white/10 p-6 hover:border-[#D4AF37]/30 transition-all duration-300 group h-full flex flex-col"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        whileHover={{ y: -5 }}
      >
        <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center ${colors.border} border mb-4`}>
          <resource.icon className={`h-6 w-6 ${colors.text}`} />
        </div>
        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
          {resource.title}
        </h3>
        <p className="text-white/50 text-sm mb-4 flex-1">
          {resource.description}
        </p>
        <button className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium group-hover:gap-3 transition-all">
          View {type === 'guide' ? 'Guide' : type === 'tool' ? 'Tool' : 'Checklist'}
          <ArrowRight className="h-4 w-4" />
        </button>
      </motion.div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Investor Resources for Real Estate Investing | ASAP Capital</title>
        <meta 
          name="description" 
          content="Educational resources, guides, and tools for investors exploring private equity real estate investments with ASAP Capital." 
        />
        <meta name="keywords" content="investor resources, real estate investing guides, private equity education, investment tools, due diligence checklists" />
        <link rel="canonical" href="/resources" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                {/* Header */}
                <div className="max-w-3xl">
                  <motion.div 
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                    variants={itemVariants}
                  >
                    <GraduationCap className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Investor Education</span>
                  </motion.div>

                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                    variants={itemVariants}
                  >
                    Investor Resources for{' '}
                    <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                      Private Equity
                    </span>{' '}
                    Real Estate
                  </motion.h1>

                  <motion.div 
                    className="space-y-4 text-white/60 text-base lg:text-lg leading-relaxed"
                    variants={itemVariants}
                  >
                    <p>
                      ASAP Capital provides educational tools and reference materials designed to help 
                      investors better understand private equity real estate investing, 
                      fund structures, and value-add investment strategies.
                    </p>
                    <p>
                      These resources are intended to support investor education, due diligence, 
                      and informed decision-making when evaluating real estate investment opportunities.
                    </p>
                  </motion.div>

                  <motion.p 
                    className="mt-6 text-white/40 text-sm italic border-l-2 border-[#D4AF37]/30 pl-4"
                    variants={itemVariants}
                  >
                    All materials are provided for informational purposes only and do not constitute investment advice.
                  </motion.p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== GUIDES & WHITE PAPERS ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-4" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                    <BookOpen className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Investment Guides and White Papers
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/50 max-w-2xl mb-8"
                  variants={itemVariants}
                >
                  Our investment guides provide plain-language explanations of private equity real estate 
                  strategies, risk considerations, and capital structures. These resources are designed 
                  to help investors understand how value-add real estate investments operate and how 
                  returns are generated through operational execution.
                </motion.p>

                <div className="grid md:grid-cols-3 gap-6">
                  {guides.map((guide) => (
                    <ResourceCard key={guide.title} resource={guide} type="guide" />
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== TOOLS & CALCULATORS ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-4" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                    <Calculator className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Investment Tools and Calculators
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/50 max-w-2xl mb-8"
                  variants={itemVariants}
                >
                  Investment analysis tools are designed to help investors understand how cash flow, 
                  hold periods, and capital structures affect potential outcomes. These tools are for 
                  educational use only and should not be interpreted as return forecasts.
                </motion.p>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  {tools.map((tool, index) => (
                    <motion.div
                      key={tool.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl border border-white/10 p-6 hover:border-emerald-500/30 transition-all duration-300 group"
                      variants={itemVariants}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 mb-4">
                        <tool.icon className="h-6 w-6 text-emerald-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                        {tool.title}
                      </h3>
                      <p className="text-white/50 text-sm">
                        {tool.description}
                      </p>
                    </motion.div>
                  ))}
                </div>

                <motion.div className="text-center" variants={itemVariants}>
                  <Button className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full px-8">
                    Access Tools
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== DUE DILIGENCE CHECKLISTS ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-4" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <ClipboardCheck className="h-5 w-5 text-blue-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Due Diligence Checklists
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/50 max-w-2xl mb-8"
                  variants={itemVariants}
                >
                  Evaluating private equity real estate investments requires reviewing sponsor experience, 
                  market fundamentals, and deal structure. These checklists help investors organize 
                  questions and documentation when reviewing investment opportunities.
                </motion.p>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  {checklists.map((checklist, index) => (
                    <motion.div
                      key={checklist.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl border border-white/10 p-6 hover:border-blue-500/30 transition-all duration-300 group"
                      variants={itemVariants}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 mb-4">
                        <checklist.icon className="h-6 w-6 text-blue-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                        {checklist.title}
                      </h3>
                      <p className="text-white/50 text-sm mb-4">
                        {checklist.description}
                      </p>
                      <button className="inline-flex items-center gap-2 text-blue-400 text-sm font-medium group-hover:gap-3 transition-all">
                        <Download className="h-4 w-4" />
                        Download PDF
                      </button>
                    </motion.div>
                  ))}
                </div>

                <motion.div className="text-center" variants={itemVariants}>
                  <Button className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-full px-8">
                    Download All Checklists
                    <Download className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FUND STRUCTURES ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                {/* Left - Content */}
                <motion.div variants={itemVariants}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                      <PieChart className="h-5 w-5 text-purple-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Fund Structures and Investor Rights
                    </h2>
                  </div>

                  <div className="space-y-4 text-white/60 leading-relaxed mb-6">
                    <p>
                      Private equity real estate investments are typically structured as limited 
                      partnerships or limited liability companies. Investors participate as limited 
                      partners, while the sponsor manages acquisitions, operations, and dispositions.
                    </p>
                    <p>
                      Educational materials in this section explain:
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {fundTopics.map((topic, index) => (
                      <motion.li 
                        key={topic}
                        className="flex items-center gap-3"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <CheckCircle2 className="h-5 w-5 text-purple-400 flex-shrink-0" />
                        <span className="text-white/70">{topic}</span>
                      </motion.li>
                    ))}
                  </ul>

                  <p className="text-white/40 text-sm mb-6">
                    For official investment terms, investors should always reference offering documents 
                    and the Private Placement Memorandum (PPM).
                  </p>

                  <Link to="/fund-terms">
                    <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold rounded-full">
                      View Fund Terms
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>

                {/* Right - Visual */}
                <motion.div 
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10"
                  variants={itemVariants}
                >
                  <h4 className="text-white font-semibold mb-6 flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-purple-400" />
                    Typical LP Fund Structure
                  </h4>
                  <div className="space-y-4">
                    {[
                      { label: 'General Partner (GP)', description: 'Manages fund operations and investments', percent: '1-5%' },
                      { label: 'Limited Partners (LPs)', description: 'Passive investors providing capital', percent: '95-99%' },
                      { label: 'Preferred Return', description: 'Priority distribution to LPs', percent: '8%' },
                      { label: 'Carried Interest', description: 'GP share of profits above hurdle', percent: '20%' }
                    ].map((item, index) => (
                      <div 
                        key={item.label}
                        className="flex items-start justify-between p-4 rounded-xl bg-white/5 border border-white/5"
                      >
                        <div>
                          <p className="text-white font-medium">{item.label}</p>
                          <p className="text-white/40 text-sm">{item.description}</p>
                        </div>
                        <span className="text-purple-400 font-bold">{item.percent}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MARKET EDUCATION ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="text-center mb-10" variants={itemVariants}>
                  <div className="inline-flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                      <LineChart className="h-5 w-5 text-cyan-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Market Education and Research
                    </h2>
                  </div>
                  <p className="text-white/50 max-w-2xl mx-auto">
                    Understanding market fundamentals is critical to evaluating real estate investments. 
                    Investors can review current market data, rental trends, and economic indicators 
                    through our research and insights platform.
                  </p>
                  <p className="text-white/40 text-sm mt-4">
                    These resources focus primarily on Houston and key Texas markets where we actively invest.
                  </p>
                </motion.div>

                <motion.div 
                  className="grid md:grid-cols-2 gap-6"
                  variants={itemVariants}
                >
                  <Link to="/market-reports">
                    <motion.div
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10 hover:border-cyan-500/30 transition-all group h-full"
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20 mb-6">
                        <BarChart3 className="h-7 w-7 text-cyan-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                        Market Reports
                      </h3>
                      <p className="text-white/50 mb-4">
                        Quarterly Houston market reports covering multifamily occupancy, rent trends, 
                        and commercial real estate fundamentals.
                      </p>
                      <span className="inline-flex items-center gap-2 text-cyan-400 font-medium group-hover:gap-3 transition-all">
                        View Market Reports <ArrowRight className="h-4 w-4" />
                      </span>
                    </motion.div>
                  </Link>

                  <Link to="/insights">
                    <motion.div
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10 hover:border-[#D4AF37]/30 transition-all group h-full"
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20 mb-6">
                        <BookOpen className="h-7 w-7 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
                        Market Insights
                      </h3>
                      <p className="text-white/50 mb-4">
                        Articles and analysis on Houston real estate trends, investment strategies, 
                        and economic indicators affecting property values.
                      </p>
                      <span className="inline-flex items-center gap-2 text-[#D4AF37] font-medium group-hover:gap-3 transition-all">
                        Read Market Insights <ArrowRight className="h-4 w-4" />
                      </span>
                    </motion.div>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== CASE STUDIES ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="text-center mb-10" variants={itemVariants}>
                  <div className="inline-flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                      <Briefcase className="h-5 w-5 text-amber-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Learn From Our Case Studies
                    </h2>
                  </div>
                  <p className="text-white/50 max-w-2xl mx-auto">
                    Our case studies provide real-world examples of how value-add strategies are 
                    executed across multifamily and commercial properties. These examples highlight 
                    acquisition strategies, renovation programs, and operational improvements.
                  </p>
                </motion.div>

                <motion.div 
                  className="flex flex-col sm:flex-row gap-4 justify-center"
                  variants={itemVariants}
                >
                  <Link to="/case-studies">
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8 py-3 h-12 rounded-full shadow-lg shadow-[#D4AF37]/20">
                        View Case Studies
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </motion.div>
                  </Link>
                  <Link to="/portfolio">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-12 rounded-full">
                      View Portfolio
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== SPEAK WITH INVESTOR RELATIONS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                {/* Background accents */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 text-center max-w-2xl mx-auto">
                  <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20 mx-auto mb-6">
                    <Users className="h-8 w-8 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                    Speak With Investor Relations
                  </h2>
                  <p className="text-white/50 mb-8">
                    If you would like to discuss our investment approach, reporting practices, or 
                    upcoming opportunities, our investor relations team is available to answer 
                    general questions and provide additional educational materials.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/contact">
                      <button className="cta-contact px-8 py-3 h-12 rounded-full w-full sm:w-auto">
                        Schedule Consultation
                        <Calendar className="h-4 w-4" />
                      </button>
                    </Link>
                    <Link to="/contact">
                      <button className="cta-contact px-8 py-3 h-12 rounded-full w-full sm:w-auto">
                        Request Materials
                        <FileText className="h-4 w-4" />
                      </button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== QUICK LINKS ==================== */}
          <section className="py-12 relative border-t border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <p className="text-white/40 text-sm mb-4">Related Pages</p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { name: 'Fund Terms', href: '/fund-terms' },
                    { name: 'Investment Strategy', href: '/strategy' },
                    { name: 'Investment Process', href: '/investment-process' },
                    { name: 'Track Record', href: '/track-record' },
                    { name: 'FAQ', href: '/faq' }
                  ].map((link) => (
                    <Link 
                      key={link.name}
                      to={link.href}
                      className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== COMPLIANCE FOOTER ==================== */}
          <section className="py-8 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="bg-white/[0.02] border border-white/5 rounded-xl p-5"
              >
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-white/30 flex-shrink-0 mt-0.5" />
                  <p className="text-white/40 text-xs leading-relaxed">
                    <strong className="text-white/50">Important Disclosure:</strong> These materials 
                    are provided for informational purposes only and do not constitute investment, 
                    legal, or tax advice. Investment offerings are made only through official offering 
                    documents and applicable Private Placement Memoranda (PPMs). All investments involve risk.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default InvestorResourcesPage;
